import { useState } from 'react';
import { Bell, Lock, MapPin, Pencil, Repeat, Trash2, Users } from 'lucide-react';
import {
  describeLeadTime,
  describeRRule,
  formatTime,
  isEventEditable,
  parseRRule,
  shortDate,
  weekdayName,
  type EventOccurrence,
} from '@awwyeah/shared';
import { ownerNames, styleForOwners, type MemberView } from '../members';
import { MemberStack } from './MemberBits';
import { Sheet } from './Sheet';
import type { SaveScope } from './EventFormSheet';

/**
 * "Who can see this" for one event (WS-15). Renders nothing in the ordinary case, because
 * "everyone" is the default and a badge on every event would be noise.
 *
 * The two cases say genuinely different things, and the copy is careful not to blur them:
 * a personal event is enforced by the server; a subset audience is a display filter over a
 * shared document.
 */
function AudienceNote({
  event,
  members,
  personal,
}: {
  event: EventOccurrence['event'];
  members: ReadonlyMap<string, MemberView>;
  personal: boolean;
}): JSX.Element | null {
  if (personal) {
    return (
      <p
        className="flex items-center gap-2 rounded-sm border border-line px-3 py-2 text-xs text-fg-2"
        data-testid="event-audience-note"
        data-audience="private"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Just you — this one is on your
        own calendar, nobody else can open it.
      </p>
    );
  }
  if (!event.audience) return null;
  const names = event.audience.map((id) => members.get(id)?.name ?? 'someone').join(', ');
  return (
    <p
      className="flex items-center gap-2 rounded-sm border border-line px-3 py-2 text-xs text-fg-2"
      data-testid="event-audience-note"
      data-audience="members"
    >
      <Users className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Shown to {names || 'nobody'} —
      hidden from the others&apos; calendars, but not hidden from the shared calendar itself.
    </p>
  );
}

/**
 * Event detail sheet (design Screen C · 7): peek height — only as tall as its content —
 * with the remote-change line explaining itself instead of a badge.
 */
export function EventDetailSheet({
  occurrence,
  members,
  now,
  onClose,
  onEdit,
  onDelete,
  personal = false,
}: {
  occurrence: EventOccurrence | null;
  members: ReadonlyMap<string, MemberView>;
  now: Date;
  onClose: () => void;
  onEdit: (occurrence: EventOccurrence) => void;
  onDelete: (occurrence: EventOccurrence, scope: SaveScope) => void;
  /** True when this event lives in my personal calendar doc (WS-15). */
  personal?: boolean;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);

  if (!occurrence) return <Sheet open={false} onClose={onClose} label="Event" children={null} />;

  const { event } = occurrence;
  const style = styleForOwners(event.ownerIds, members);
  const rule = parseRRule(event.recurrence);
  const editable = isEventEditable(event);
  const editor = members.get(event.updatedBy);
  const start = new Date(occurrence.start);

  return (
    <>
      <Sheet open onClose={onClose} label={event.title} testId="event-sheet">
        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <div className="flex items-start gap-3">
            <span className="mt-1 h-10 w-1 shrink-0 rounded-sm" style={style.dot} />
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-xl leading-tight">{event.title}</h2>
              <p className="pt-1 text-sm text-fg-3">
                {weekdayName(start).slice(0, 3)}, {shortDate(start, now)} ·{' '}
                {event.allDay
                  ? 'all day'
                  : `${formatTime(occurrence.start)} – ${formatTime(occurrence.end)}`}
              </p>
            </div>
            <MemberStack ownerIds={event.ownerIds} byId={members} size="lg" />
          </div>

          {editor && (
            <p
              className="rounded-sm bg-highlight px-3 py-2 text-xs font-semibold"
              style={style.text}
            >
              {editor.name} last changed this · {relativeTime(event.updatedAt, now)}
            </p>
          )}

          <dl className="flex flex-col gap-2 border-t border-line pt-3 text-sm">
            {ownerNames(event.ownerIds, members) && (
              <Row icon={null} text={ownerNames(event.ownerIds, members)} />
            )}
            {event.location && <Row icon={<MapPin className="h-4 w-4" />} text={event.location} />}
            {rule && <Row icon={<Repeat className="h-4 w-4" />} text={describeRRule(rule)} />}
            {event.reminders.length > 0 && (
              <Row
                icon={<Bell className="h-4 w-4" />}
                text={event.reminders
                  .map((reminder) => describeLeadTime(reminder.minutesBefore))
                  .join(' · ')}
              />
            )}
          </dl>

          <AudienceNote event={event} members={members} personal={personal} />

          {event.notes && <p className="whitespace-pre-wrap text-sm text-fg-2">{event.notes}</p>}

          {editable ? (
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                data-testid="event-edit"
                onClick={() => onEdit(occurrence)}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md bg-inverse text-sm font-semibold text-inverse-fg"
              >
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button
                type="button"
                data-testid="event-delete"
                aria-label="Delete event"
                onClick={() => (rule ? setConfirming(true) : onDelete(occurrence, 'all'))}
                className="flex h-12 w-12 items-center justify-center rounded-md border border-line text-danger"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="text-xs text-fg-3">From an external calendar — read-only here.</p>
          )}
        </div>
      </Sheet>

      <Sheet
        open={confirming}
        onClose={() => setConfirming(false)}
        label="Delete repeating event"
        testId="delete-scope-sheet"
      >
        <div className="flex flex-col gap-3 p-4">
          <p className="font-display text-xl">This repeats</p>
          <p className="text-sm text-fg-2">Delete just this one, or the whole series?</p>
          <button
            type="button"
            data-testid="delete-this"
            onClick={() => {
              setConfirming(false);
              onDelete(occurrence, 'this');
            }}
            className="h-12 rounded-md border border-line-strong bg-surface text-sm font-semibold"
          >
            Just this one
          </button>
          <button
            type="button"
            data-testid="delete-all"
            onClick={() => {
              setConfirming(false);
              onDelete(occurrence, 'all');
            }}
            className="h-12 rounded-md bg-danger text-sm font-semibold text-inverse-fg"
          >
            Delete all of them
          </button>
        </div>
      </Sheet>
    </>
  );
}

function Row({ icon, text }: { icon: React.ReactNode; text: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-fg-2">
      <span className="text-fg-3">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

/** "2m ago" / "just now" — the attribution the design brief asks for (§3.4). */
export function relativeTime(iso: string, now: Date): string {
  const deltaSeconds = Math.round((now.getTime() - Date.parse(iso)) / 1000);
  if (deltaSeconds < 60) return 'just now';
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
  if (deltaSeconds < 86_400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
  return `${Math.floor(deltaSeconds / 86_400)}d ago`;
}
